/** @format */

import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import {
	MapPin,
	Phone,
	Mail,
	Calendar,
	Clock,
	ArrowRight,
	Info,
} from "lucide-react"; // Refined Icons
import { PopupModal } from "react-calendly";

// Components (Ensure paths are correct)
import ContactForm from "../../components/contact/ContactForm";
import GoogleMap from "../../components/GoogleMap";

// --- Reusable Info Card (Revised for more flexibility) ---
const InfoCard = ({ icon: Icon, title, children, className = "" }) => (
	<div
		className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6 ${className}`}
	>
		<div className="flex items-center gap-3 mb-3">
			<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
				<Icon className="w-6 h-6 text-primary" />
			</div>
			<h3 className="font-semibold text-lg text-gray-800">{title}</h3>
		</div>
		<div className="text-sm text-gray-600 space-y-1 pl-0 sm:pl-13">
			{children}
		</div>{" "}
		{/* Indent content */}
	</div>
);

// --- Main Contact Page ---
const ContactPage = () => {
	const [openBooking, setOpenBooking] = useState(false); // State for Calendly modal

	// --- Contact Details (Centralized) ---
	const contactDetails = {
		address: "50 Hollinger Road, Toronto, ON M4B 3G5, Canada",
		addressLink:
			"https://www.google.com/maps/search/?api=1&query=Off+Cut+Shades+50+Hollinger+Road+Toronto", // Link for Google Maps
		phone1: "416-266-3501",
		phone2: "1-800-668-1728",
		email: "info@offcutshades.com",
		hours: "Monday - Friday, 8:30 AM - 5:00 PM ET",
		emailResponse: "We typically respond within 1 business day.",
		calendlyUrl: "https://calendly.com/offcutshades",
	};

	return (
		<>
			<Helmet>
				<title>Contact Off Cut Shades | Expert Support for Window Blinds</title>{" "}
				{/* SEO Title */}
				<meta
					name="description"
					content="Get in touch with Off Cut Shades for expert advice on custom window shades and blinds. Contact us via phone, email, online form, or visit our Toronto showroom."
				/>{" "}
				{/* SEO Desc */}
				<link rel="canonical" href="https://offcutshades.com/contact" />
				{/* Removed Breadcrumb schema - can add back if needed elsewhere */}
			</Helmet>

			{/* Use standard background */}
			<main className="bg-gray-50 pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				{/* --- Hero Section --- */}
				<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
						className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
					>
						<Mail size={36} strokeWidth={1.5} /> {/* Use Mail or Phone icon */}
					</motion.div>
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
					>
						Get In Touch
					</motion.h1>
					<motion.p
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.3 }}
						className="text-lg text-gray-600 max-w-2xl mx-auto"
					>
						Have questions? Our window treatment experts are ready to help.
						Choose your preferred way to connect with us below.
					</motion.p>
				</section>
				{/* --- Contact Options Grid --- */}
				<section className="container mx-auto px-4 max-w-5xl mx-auto mb-12 md:mb-16">
					{/* Grid for key contact methods */}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
						{/* Email Card */}
						<InfoCard icon={Mail} title="Send Us an Email">
							<a
								href={`mailto:${contactDetails.email}`}
								className="font-medium text-primary hover:underline break-all"
							>
								{contactDetails.email}
							</a>
							<p className="text-xs text-gray-500">
								{contactDetails.emailResponse}
							</p>
							{/* Simple action link */}
							<a
								href={`mailto:${contactDetails.email}`}
								className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
							>
								Write Email <ArrowRight size={14} />
							</a>
						</InfoCard>

						{/* Phone Card */}
						<InfoCard icon={Phone} title="Give Us a Call">
							<a
								href={`tel:${contactDetails.phone1}`}
								className="font-medium text-primary hover:underline"
							>
								{contactDetails.phone1}
							</a>
							<br />
							<span className="text-xs text-gray-500">or Toll-Free: </span>
							<a
								href={`tel:${contactDetails.phone2}`}
								className="font-medium text-primary hover:underline"
							>
								{contactDetails.phone2}
							</a>
							<p className="text-xs text-gray-500 pt-1">
								{contactDetails.hours}
							</p>
							{/* Simple action link */}
							<a
								href={`tel:${contactDetails.phone1}`}
								className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
							>
								Call Now <ArrowRight size={14} />
							</a>
						</InfoCard>

						{/* Book Consultation Card */}
						<InfoCard icon={Calendar} title="Book Consultation">
							<p>
								Schedule a free virtual or showroom consultation with one of our
								experts via Calendly.
							</p>
							{/* Button to open modal */}
							<button
								onClick={() => setOpenBooking(true)}
								className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
							>
								Book Online <ArrowRight size={14} />
							</button>
						</InfoCard>
					</div>
				</section>
				{/* --- Section: Contact Form --- */}
				<section className="container mx-auto px-4 max-w-5xl mx-auto mb-12 md:mb-16">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.5 }}
						className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8"
					>
						<h2 className="text-2xl font-semibold text-gray-800 mb-5 text-center md:text-left">
							Send Us a Message
						</h2>
						<ContactForm landing={false} /> {/* Embed the form directly */}
					</motion.div>
				</section>
				{/* --- Section: Showroom & Map --- */}
				<section className="container mx-auto px-4 max-w-5xl mx-auto mb-12 md:mb-16">
					<div className="grid grid-cols-1 lg:grid-cols-[1fr,1.5fr] gap-6 md:gap-8 items-center bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
						{/* Showroom Info */}
						<motion.div
							initial={{ opacity: 0, x: -20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="p-6 md:p-8 order-2 lg:order-1"
						>
							<h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
								<MapPin size={20} className="text-primary" /> Visit Our Showroom
							</h2>
							<p className="text-sm text-gray-600 mb-4">
								Experience our products firsthand! Walk-ins are welcome during
								business hours.
							</p>
							{/* Address */}
							<address className="text-sm text-gray-700 not-italic mb-4 border-b pb-4">
								<a
									href={contactDetails.addressLink}
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-primary transition-colors"
								>
									{contactDetails.address}
								</a>
							</address>
							{/* Hours */}
							<div>
								<h3 className="text-base font-medium text-gray-800 mb-2 flex items-center gap-1.5">
									<Clock size={16} className="text-gray-500" /> Showroom Hours
								</h3>
								<div className="space-y-1 text-sm">
									<div className="flex justify-between">
										<span>Monday - Friday</span>
										<span>8:30 AM - 5:00 PM</span>
									</div>
									<div className="flex justify-between text-gray-500">
										<span>Saturday - Sunday</span>
										<span>Closed</span>
									</div>
								</div>
							</div>
							{/* Link to book */}
							<button
								onClick={() => setOpenBooking(true)}
								className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
							>
								Book Showroom Visit <ArrowRight size={14} />
							</button>
						</motion.div>

						{/* Map */}
						<motion.div
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="relative h-64 md:h-80 lg:h-full order-1 lg:order-2 min-h-[300px]" // Ensure min-height
						>
							<GoogleMap /> {/* Assumes GoogleMap fills its container */}
						</motion.div>
					</div>
				</section>
				{/* --- Calendly Modal --- */}
				<PopupModal
					url={contactDetails.calendlyUrl} // Use URL from centralized details
					rootElement={document.getElementById("root")} // Ensure 'root' element exists
					open={openBooking}
					onModalClose={() => setOpenBooking(false)}
					// Use default styles or pass props if needed
				/>
				{/* Removed separate Contact Form Modal */}
			</main>
		</>
	);
};

export default ContactPage;
